import { Component, OnDestroy } from '@angular/core';
import { filter, map, Observable, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from '../../../base.component';
import { HelpService } from '../../../../services/help.service';
import { InsightsUtility } from '../../../../utilities/insights/insights.utility';
import { GeneralError } from '../../../../insights/errors/general.error';
import { UploadWindowClosedEvent } from '../../../../insights/events/upload-window-closed.event';
import { ActivatedRoute } from '@angular/router';
import { DatMapStore } from '@store/component/dat-map.store';
import { DatColumnMetadata, DatMapMetadataItem, LexbeField } from '@models/resource/dat-map.resource';
import { CaseHubSdk } from '@sdks/case.hub-sdk';
import { ProcessingStepCount } from '@managers/case.manager';
import { take, tap } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LastRunReviewDialogComponent } from './last-run-review-dialog/last-run-review-dialog.component';
import { Role } from '@models/role.model';
import { AddFieldComponent } from '@dialogs/add-field/add-field.component';
import { CodingField } from '@models/field/coding-field.model';
import { UserConfigsService } from '@services/user-configs.service';
import { FieldType } from '@models/field/field-type.enum';
import { BuiltInFieldId } from '@models/built-in-field-id.enum';

@Component({
    selector: 'lex-dat-metadata-mapping',
    templateUrl: './dat-metadata-mapping.component.html',
    styleUrls: ['./dat-metadata-mapping.component.scss'],
    standalone: false
})
export class DatMetadataMappingComponent extends BaseComponent implements OnDestroy {
	public FieldType = FieldType;
	public BuiltInFieldId = BuiltInFieldId;
	public columnsToDisplay = [
		'dat-column',
		'lexbe-field',
		'sample-data1',
		'sample-data2',
		'sample-data3'
	];
	public uploadBatchId: string;
	public dataSource$: Observable<DatColumnMetadata[]>;
	public totalEntries$: Observable<number>;
	public lexbeFields$: Observable<LexbeField[]>;
	private actualMappings: DatMapMetadataItem[] = [];
	public isApplyingMetadata: boolean = false;
	public loading: boolean = false;
	public error: boolean = false;
	public previousReport?: string;
	public uploadBatchTitle?: string;

	public readonly Role = Role;

	constructor(
		private datMapStore: DatMapStore,
		private route: ActivatedRoute,
		private helpService: HelpService,
		private insightsUtility: InsightsUtility,
		private caseHubSdk: CaseHubSdk,
		private dialog: MatDialog,
		public ucs: UserConfigsService
	) {
		super();
		this.route.params.subscribe(params => {
			this.uploadBatchId = params['id'];
			this.datMapStore.clearState();
			this.datMapStore.getState(this.uploadBatchId);
		});
		this.caseHubSdk.processing$.pipe(takeUntil(this.destroy$)).subscribe(steps => this.checkMetadataProcessingAndRefresh(steps));
		this.datMapStore.uploadBatchTitle$.pipe(takeUntil(this.destroy$)).subscribe(title => this.uploadBatchTitle = title);
		this.datMapStore.loading$.pipe(takeUntil(this.destroy$)).subscribe(loading => this.loading = loading);
		this.datMapStore.error$.pipe(takeUntil(this.destroy$)).subscribe(error => this.error = error);
		this.dataSource$ = this.datMapStore.datColumnMetadata$;
		this.lexbeFields$ = this.datMapStore.lexbeFields$;
		this.datMapStore.actualMappings$.pipe(takeUntil(this.destroy$)).subscribe(m => this.actualMappings = m);
		this.totalEntries$ = this.datMapStore.mappingCount$;
		this.datMapStore.lastRunReport$.pipe(takeUntil(this.destroy$)).subscribe(r => {
			this.previousReport = r;
			if(r)
				this.openReportDialog();
		});

		this.helpService.updateHelpLinkUrl('https://sites.google.com/a/ediscoveryplatform.com/help/home/help/case-menu/add-documents/processed-documents');
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	public lexbeFieldFromDatColumn(datColumn: string): string {
		return this.actualMappings.find(m => m.datMapColumns.includes(datColumn))?.lexbeField?.fieldId ?? '-- ignore --';
	}

	public assignLexbeField($event, element: DatColumnMetadata) {
		const theField$ = this.lexbeFields$.pipe(take(1), map(x => x.find(f => f.fieldId === $event.value)), takeUntil(this.destroy$));
		theField$.subscribe(lexbeField => {
			let newMapping: DatMapMetadataItem;
			if (lexbeField == undefined) { // "Delete", they clicked -- ignore --
				newMapping = { lexbeField: undefined, datMapColumns: [element.datColumn] } as DatMapMetadataItem;
			} else {
				newMapping = { lexbeField: lexbeField, datMapColumns: [element.datColumn] } as DatMapMetadataItem;
			}
			this.datMapStore.updateActualMapping(newMapping);
		});
	}

	public addNewField(element: DatColumnMetadata) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = true;
		this.dialog.open(AddFieldComponent, dialogConfig).afterClosed().pipe(
			take(1),
			filter((savedField: CodingField) => !!savedField), // Proceed only if savedField is truthy (Cancel was not clicked)
			tap((savedField: CodingField) => {
				const lexbeField = {
					fieldId: savedField.fieldId,
					displayTitle: savedField.displayTitle,
					databaseTitle: savedField.displayTitle,
					type: savedField.type,
					isDatMapOption: true,
					disabled: false
				} as LexbeField;

				if (lexbeField.type !== FieldType.DropDownList
					&& lexbeField.type !== FieldType.SharedCheckBox
					&& lexbeField.type !== FieldType.SectionSeparator) {
					this.datMapStore.addLexbeField(lexbeField);
				}
			}),
			switchMap((savedField: CodingField) => this.lexbeFields$.pipe(
				take(1),
				map(fields => fields.find(f => f.fieldId === savedField.fieldId))
			)),
			takeUntil(this.destroy$)
		).subscribe((lexbeField) => {
			if (lexbeField) {
				const newMapping: DatMapMetadataItem = {
					lexbeField: lexbeField,
					datMapColumns: [element.datColumn]
				} as DatMapMetadataItem;

				this.datMapStore.updateActualMapping(newMapping);
				this.apply(); // Persist adding coding field to dat map metadata
			}
		});
	}

	public reset() {
		this.datMapStore.resetMappingsToDefault();
	}

	public applyAndRun() {
		this.datMapStore.putState({uploadBatchId: this.uploadBatchId, runDatMap: true});
		this.isApplyingMetadata = true;
	}
	public apply() {
		this.datMapStore.putState({uploadBatchId: this.uploadBatchId, runDatMap: false});
	}

	public startNewDat(uploadBatchId: string) {
		const newWindow = window.open(`/upload/${uploadBatchId}/dat`, 'UploadWindow', 'status=no,location=no,menubar=no,directories=no,toolbar=no,width=950,height=600');
		newWindow.focus();
		const timer = setInterval(() => {
			try {
				if (newWindow.closed) {
					this.insightsUtility.event(new UploadWindowClosedEvent());
					clearInterval(timer);
				}
			} catch (e) {
				this.insightsUtility.event(new GeneralError({
					internalError: e,
					message: 'Issue monitoring upload child window.'
				}));
				clearInterval(timer);
			}
		}, 500);
	}

	private checkMetadataProcessingAndRefresh(steps: ProcessingStepCount[]) {
		const isApplyingMetadata = !!steps?.some(s => s.functionName.includes('Metadata'));
		if (this.isApplyingMetadata && !isApplyingMetadata)
			this.datMapStore.getState(this.uploadBatchId);
		this.isApplyingMetadata = isApplyingMetadata;
	}

	public openReportDialog() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.data = this.previousReport;
		this.dialog.open(LastRunReviewDialogComponent, dialogConfig).afterClosed().subscribe();
	}
}
